import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { API_BASE } from '../lib/env';

const STORAGE_KEY = 'skylux_admin_token';

type AuthStatus = 'loading' | 'unauthenticated' | 'authenticated';

type AuthContextValue = {
  status: AuthStatus;
  username: string | null;
  token: string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

async function fetchSession(token: string): Promise<{ username: string } | null> {
  const res = await fetch(`${API_BASE}/api/auth/session`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = (await res.json().catch(() => ({}))) as { ok?: boolean; username?: string };
  if (!res.ok) {
    sessionStorage.removeItem(STORAGE_KEY);
    return null;
  }
  if (data.ok === true && typeof data.username === 'string') {
    return { username: data.username };
  }
  sessionStorage.removeItem(STORAGE_KEY);
  return null;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [username, setUsername] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (!stored) {
        if (!cancelled) {
          setStatus('unauthenticated');
          setToken(null);
          setUsername(null);
        }
        return;
      }
      const session = await fetchSession(stored);
      if (cancelled) return;
      if (session) {
        setToken(stored);
        setUsername(session.username);
        setStatus('authenticated');
      } else {
        setToken(null);
        setUsername(null);
        setStatus('unauthenticated');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (u: string, p: string) => {
    const res = await fetch(`${API_BASE}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: u, password: p }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(typeof body.message === 'string' ? body.message : 'Login failed');
    }
    const nextToken = typeof body.token === 'string' ? body.token : null;
    const name = typeof body.username === 'string' ? body.username : u;
    if (!nextToken) throw new Error('Invalid server response');
    sessionStorage.setItem(STORAGE_KEY, nextToken);
    setToken(nextToken);
    setUsername(name);
    setStatus('authenticated');
  }, []);

  const logout = useCallback(async () => {
    const t = sessionStorage.getItem(STORAGE_KEY);
    if (t) {
      await fetch(`${API_BASE}/api/auth/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${t}` },
      }).catch(() => {});
    }
    sessionStorage.removeItem(STORAGE_KEY);
    setToken(null);
    setUsername(null);
    setStatus('unauthenticated');
  }, []);

  const value = useMemo(
    () => ({ status, username, token, login, logout }),
    [status, username, token, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
